import { Fragment, useState } from "react";
import type { Theme } from "./theme";

type Status = "recommend" | "consider";

type Candidate = {
  id: string;
  name: string;
  role: string;
  loc: string;
  scores: Record<string, number>;
  status: Status;
  summary: string;
  flags: string[];
  evidence: number;
};

export const CANDIDATES: Candidate[] = [
  {
    id: "ap",
    name: "Amara Pace",
    role: "VP Engineering",
    loc: "London",
    scores: {
      "Systems leadership": 9,
      "Platform scale": 8,
      "Team growth": 9,
      "Commercial judgement": 7,
    },
    status: "recommend",
    summary: "Led platform rebuild at Series C scale. Strong alignment with role blueprint.",
    flags: [],
    evidence: 4,
  },
  {
    id: "jk",
    name: "Jonas Kvist",
    role: "VP Engineering",
    loc: "Berlin",
    scores: {
      "Systems leadership": 8,
      "Platform scale": 9,
      "Team growth": 6,
      "Commercial judgement": 8,
    },
    status: "consider",
    summary: "Deep infra background. Open question on people-growth experience.",
    flags: ["Team growth evidence thin"],
    evidence: 3,
  },
  {
    id: "rs",
    name: "Rhea Soto",
    role: "VP Engineering",
    loc: "Remote",
    scores: {
      "Systems leadership": 7,
      "Platform scale": 6,
      "Team growth": 9,
      "Commercial judgement": 9,
    },
    status: "consider",
    summary: "Strong commercial instincts; earlier-stage profile than blueprint calls for.",
    flags: ["Stage fit"],
    evidence: 3,
  },
];

export function CandidateComparison({ theme }: { theme: Theme }) {
  const [selected, setSelected] = useState<Record<string, boolean>>({
    ap: true,
    jk: true,
    rs: true,
  });
  const active = CANDIDATES.filter((c) => selected[c.id]);
  const criteria = Object.keys(CANDIDATES[0].scores);

  const statusPill = (s: Status) => {
    if (s === "recommend") return { bg: theme.accentSoft, fg: theme.accentFg, label: "Recommend" };
    return { bg: theme.border, fg: theme.fgMute, label: "Consider" };
  };

  return (
    <div
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius * 1.5,
        background: theme.bg,
        overflow: "hidden",
      }}
    >
      {/* Window chrome */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          borderBottom: `1px solid ${theme.border}`,
          background: theme.surface,
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 5, background: theme.border }} />
          <span style={{ width: 10, height: 10, borderRadius: 5, background: theme.border }} />
          <span style={{ width: 10, height: 10, borderRadius: 5, background: theme.border }} />
        </div>
        <div
          style={{
            fontFamily: theme.mono,
            fontSize: 12,
            color: theme.fgMute,
            marginLeft: 8,
          }}
        >
          app.rarion.uk <span style={{ opacity: 0.4 }}>/ roles / vp-engineering / compare</span>
        </div>
        <div style={{ flex: 1 }} />
        <div
          style={{
            fontSize: 12,
            color: theme.fgMute,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: 3, background: "#26c281" }} />
          Live · 3 stakeholders viewing
        </div>
      </div>

      {/* Toolbar */}
      <div
        style={{
          padding: "20px 24px",
          borderBottom: `1px solid ${theme.border}`,
          display: "flex",
          alignItems: "flex-end",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: theme.fgMute,
              marginBottom: 6,
            }}
          >
            Role blueprint
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: theme.fg,
              letterSpacing: -0.4,
            }}
          >
            VP Engineering · Platform
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6 }}>
          {CANDIDATES.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-pressed={!!selected[c.id]}
              aria-label={`Toggle ${c.name}`}
              onClick={() => setSelected({ ...selected, [c.id]: !selected[c.id] })}
              style={{
                padding: "6px 10px",
                fontSize: 12,
                borderRadius: theme.radius,
                border: `1px solid ${selected[c.id] ? theme.accent : theme.border}`,
                background: selected[c.id] ? theme.accentSoft : theme.bg,
                color: selected[c.id] ? theme.accentFg : theme.fgMute,
                cursor: "pointer",
                fontFamily: "inherit",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  border: `1px solid ${selected[c.id] ? theme.accent : theme.border}`,
                  background: selected[c.id] ? theme.accent : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {selected[c.id] && (
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    width="6"
                    height="6"
                    viewBox="0 0 6 6"
                    fill="none"
                    stroke={theme.onAccent}
                    strokeWidth="1.6"
                  >
                    <path d="M1 3l1.5 1.5L5 1.5" />
                  </svg>
                )}
              </span>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `200px repeat(${Math.max(active.length, 1)}, 1fr)`,
        }}
      >
        {/* Header row */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${theme.border}`,
            fontSize: 11,
            color: theme.fgMute,
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          Criteria
        </div>
        {active.map((c) => {
          const pill = statusPill(c.status);
          return (
            <div
              key={c.id}
              style={{
                padding: "16px 20px",
                borderBottom: `1px solid ${theme.border}`,
                borderLeft: `1px solid ${theme.border}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 15,
                    background: theme.accentSoft,
                    color: theme.accentFg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {c.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.fg }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: theme.fgMute }}>{c.loc}</div>
                </div>
              </div>
              <div
                style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 500,
                  background: pill.bg,
                  color: pill.fg,
                }}
              >
                {pill.label}
              </div>
            </div>
          );
        })}

        {/* Criteria rows */}
        {criteria.map((cr, ri) => (
          <Fragment key={cr}>
            <div
              style={{
                padding: "16px 20px",
                borderBottom: ri < criteria.length - 1 ? `1px solid ${theme.border}` : "none",
                fontSize: 14,
                color: theme.fg,
                display: "flex",
                alignItems: "center",
              }}
            >
              {cr}
            </div>
            {active.map((c) => {
              const score = c.scores[cr];
              const warm = score >= 8;
              return (
                <div
                  key={c.id + cr}
                  data-testid={`cell-${c.id}-${cr}`}
                  data-warm={warm || undefined}
                  style={{
                    padding: "16px 20px",
                    borderBottom: ri < criteria.length - 1 ? `1px solid ${theme.border}` : "none",
                    borderLeft: `1px solid ${theme.border}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      fontFamily: theme.mono,
                      fontSize: 15,
                      fontWeight: 600,
                      color: warm ? theme.accentFg : theme.fg,
                      minWidth: 32,
                    }}
                  >
                    {score}
                    <span style={{ color: theme.fgMute, fontWeight: 400 }}>/10</span>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      height: 4,
                      background: theme.border,
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${score * 10}%`,
                        background: warm ? theme.accent : theme.fgMute,
                        borderRadius: 2,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </Fragment>
        ))}

        {/* Summary row */}
        <div
          style={{
            padding: "16px 20px",
            borderTop: `1px solid ${theme.border}`,
            fontSize: 11,
            color: theme.fgMute,
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          Evidence
        </div>
        {active.map((c) => (
          <div
            key={`${c.id}sum`}
            style={{
              padding: "16px 20px",
              borderTop: `1px solid ${theme.border}`,
              borderLeft: `1px solid ${theme.border}`,
              fontSize: 13,
              color: theme.fgMute,
              lineHeight: 1.5,
            }}
          >
            <div style={{ marginBottom: 8 }}>{c.summary}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: 10,
                  fontSize: 11,
                  background: theme.surface,
                  border: `1px solid ${theme.border}`,
                  color: theme.fg,
                }}
              >
                {c.evidence} interviews
              </span>
              {c.flags.map((f) => (
                <span
                  key={f}
                  style={{
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontSize: 11,
                    background: theme.surface,
                    border: `1px solid ${theme.border}`,
                    color: theme.fgMute,
                  }}
                >
                  ⚑ {f}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "14px 20px",
          borderTop: `1px solid ${theme.border}`,
          background: theme.surface,
          display: "flex",
          alignItems: "center",
          gap: 14,
          fontSize: 12,
          color: theme.fgMute,
        }}
      >
        <span>Consensus view</span>
        <div style={{ display: "flex" }}>
          {["AK", "RV", "PH", "MT"].map((n, i) => (
            <div
              key={n}
              style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                background: theme.bg,
                border: `1px solid ${theme.border}`,
                fontSize: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.fg,
                marginLeft: i > 0 ? -6 : 0,
                fontWeight: 500,
              }}
            >
              {n}
            </div>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: theme.mono }}>Updated 3 min ago</span>
      </div>
    </div>
  );
}
